<template>
  <div class="mt-2 panel-compact">
    <template v-if="!enPosicionFirma">
      <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-2">
        <div>
          <h5 class="mb-1 d-flex align-items-center gap-2 section-title">
            <i class="fas fa-pen-nib"></i>
            Firmar con VidSigner
          </h5>
          <small class="text-muted">Selecciona contactos y envía el documento a VidSigner</small>
        </div>
        <div class="d-flex gap-2 mt-2 mt-sm-0">
          <button class="btn btn-primary btn-sm" @click="mostrarModal = true" :disabled="cargandoFirmantes || enviando">
            <i class="fas fa-user-plus me-1"></i>
            Añadir firmantes
          </button>
        </div>
      </div>

      <div class="d-flex justify-content-between align-items-center mb-2">
        <div class="small text-muted">Seleccionados: {{ firmantesSeleccionados.length }}</div>
      </div>

      <div class="row g-2">
        <div class="col-12">
          <div class="card shadow-sm selected-card">
            <div class="card-body py-3">
              <div class="d-flex align-items-center gap-2 mb-2">
                <i class="fas fa-list-check text-primary"></i>
                <h6 class="mb-0">Firmantes seleccionados</h6>
              </div>
              <div v-if="firmantesSeleccionados.length === 0" class="text-muted small">
                Aún no has seleccionado firmantes.
              </div>
              <ul class="list-unstyled mb-0 selected-scroll">
                <li v-for="firmante in firmantesSeleccionados" :key="`sel-${firmante.id}`"
                  class="d-flex align-items-center justify-content-between border-bottom py-2">
                  <div class="me-2">
                    <strong class="text-truncate d-block">{{ firmante.nombre }} {{ firmante.apellidos }}</strong>
                    <div class="text-muted small text-truncate">{{ firmante.email || 'Sin email' }}</div>
                  </div>
                  <button class="btn btn-sm btn-outline-danger" @click="alternarFirmante(firmante)" :disabled="enviando">
                    <i class="fas fa-times"></i>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-end mt-3">
        <button class="btn btn-primary" :disabled="firmantesSeleccionados.length === 0 || enviando" @click="continuarFirma">
          Continuar
        </button>
      </div>
    </template>

    <template v-else-if="!enResumen">
      <SelectorPosicionFirmaVidSigner
        ref="selectorPosicionRef"
        :firmantes="posicionesFirmantes"
        :registro-seleccionado="registroSeleccionado"
        :esquema-id="id || esquema?.id"
        :show-notification="showNotification"
        @posicion-guardada="onPosicionGuardada"
        @posiciones-limpias="onPosicionesLimpias"
      />
      
      <div class="d-flex justify-content-between mt-3">
        <button class="btn btn-secondary" @click="volverASeleccion">
          <i class="fas fa-arrow-left me-2"></i>
          Volver
        </button>
        <button class="btn btn-primary" :disabled="!todasPosicionesListas" @click="mostrarResumen">
          <i class="fas fa-save me-2"></i>
          Guardar posiciones
        </button>
      </div>
    </template>

    <!-- Vista de Resumen -->
    <template v-else>
      <div class="card shadow-sm mb-3">
        <div class="card-body">
          <div class="d-flex align-items-center gap-2 mb-3">
            <i class="fas fa-clipboard-check text-primary"></i>
            <h5 class="mb-0">Resumen de firmantes</h5>
          </div>
          
          <div class="alert alert-light mb-4">
            <i class="fas fa-info-circle me-2"></i>
            <strong>¿Estás seguro de enviar este documento a VidSigner?</strong>
            <p class="mb-0 mt-2">Se enviará el documento a los siguientes contactos para su firma:</p>
          </div>

          <div class="list-group">
            <div v-for="(item, index) in posicionesResumen" 
              :key="`resumen-${item.firmante.id}-${index}`"
              class="list-group-item d-flex align-items-center gap-3">
              <div class="d-flex align-items-center gap-2 flex-grow-1">
                <div class="rounded-circle d-flex align-items-center justify-content-center" 
                  :style="{ 
                    width: '40px', 
                    height: '40px', 
                    backgroundColor: colores[index % colores.length],
                    color: 'white',
                    fontWeight: 'bold'
                  }">
                  {{ index + 1 }}
                </div>
                <div class="flex-grow-1">
                  <strong>{{ item.firmante.nombre }} {{ item.firmante.apellidos }}</strong>
                  <div class="text-muted small">
                    <i class="fas fa-envelope me-1"></i>{{ item.firmante.email || 'Sin email' }}
                  </div>
                  <div class="text-muted small mt-1">
                    <i class="fas fa-file-pdf me-1"></i>
                    Página {{ item.posicion?.pagina || 'N/A' }}
                  </div>
                </div>
              </div>
              <i class="fas fa-check-circle text-success" style="font-size: 24px;"></i>
            </div>
          </div>

          <div class="d-flex justify-content-between mt-4">
            <button class="btn btn-secondary" @click="volverAPosiciones">
              <i class="fas fa-arrow-left me-2"></i>
              Volver a posiciones
            </button>
            <button class="btn btn-success" @click="confirmarEnvio" :disabled="enviando">
              <span v-if="enviando" class="spinner-border spinner-border-sm me-2" role="status"></span>
              <i class="fas fa-paper-plane me-2"></i>
              Confirmar y enviar a VidSigner
            </button>
          </div>
        </div>
      </div>
    </template>

    <div v-if="respuestaVidsigner" class="alert alert-success mt-3 d-flex align-items-center gap-2">
      <i class="fas fa-check-circle"></i>
      <div class="flex-grow-1">
        <div class="fw-bold mb-1">Enviado correctamente</div>
        <div class="small text-muted">ID: {{ respuestaVidsigner.vidsigner_id || '-' }}</div>
        <div class="small text-muted" v-if="respuestaVidsigner.url_firma">
          <a :href="respuestaVidsigner.url_firma" target="_blank" rel="noopener">Abrir enlace de firma</a>
        </div>
      </div>
    </div>

    <!-- Modal para seleccionar contactos -->
    <div class="modal fade" :class="{ show: mostrarModal, 'd-block': mostrarModal }" tabindex="-1" style="z-index: 1050; display: none;" v-show="mostrarModal">
      <div class="modal-dialog modal-xl mt-7">
        <div class="modal-content">
          <div class="modal-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div class="input-group" style="max-width: 400px;">
                <span class="input-group-text"><i class="fas fa-search"></i></span>
                <input type="text" class="form-control" v-model="busquedaFirmantes" placeholder="Buscar contacto...">
              </div>
              <div class="d-flex gap-2">
                <button class="btn btn-secondary btn-sm" @click="recargarContactos">
                  <i class="fas fa-sync-alt me-1"></i>
                  Recargar
                </button>
                <button class="btn btn-primary btn-sm" @click="abrirAgendaContactos">
                  <i class="fas fa-address-book me-1"></i>
                  Abrir agenda
                </button>
              </div>
            </div>
            <div class="list-group" style="max-height: 400px; overflow-y: auto;">
              <a v-for="(contacto, index) in firmantesFiltrados" 
                :key="`contacto-modal-${contacto.id}-${index}-${firmantesSeleccionadosIds.length}`"
                href="#" 
                class="list-group-item list-group-item-action"
                :class="{ 
                  'active': firmantesSeleccionadosIds.includes(contacto.id),
                  'bg-primary': firmantesSeleccionadosIds.includes(contacto.id),
                  'text-white': firmantesSeleccionadosIds.includes(contacto.id)
                }"
                @click.prevent="alternarFirmante(contacto)"
                style="border-left: 4px solid transparent; transition: all 0.2s ease;">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="flex-grow-1">
                    <div class="d-flex align-items-center gap-2 mb-1">
                      <strong>{{ contacto.nombre }} {{ contacto.apellidos }}</strong>
                      <i v-if="firmantesSeleccionadosIds.includes(contacto.id)" class="fas fa-check-circle" :class="firmantesSeleccionadosIds.includes(contacto.id) ? 'text-white' : 'text-success'"></i>
                    </div>
                    <div :class="firmantesSeleccionadosIds.includes(contacto.id) ? 'text-white-50' : 'text-muted'" class="small">
                      <i class="fas fa-envelope me-1"></i>{{ contacto.email || 'Sin email' }}
                    </div>
                    <div v-if="contacto.empresa" :class="firmantesSeleccionadosIds.includes(contacto.id) ? 'text-white-50' : 'text-muted'" class="small">
                      <i class="fas fa-building me-1"></i>{{ contacto.empresa }}
                    </div>
                  </div>
                </div>
              </a>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="cerrarModal">
              <i class="fas fa-times me-1"></i>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="mostrarModal" class="modal-backdrop fade show" style="z-index: 1040;"></div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, computed, onMounted, nextTick, shallowRef } from 'vue'
import axiosInstance from '@/axios'
import SelectorPosicionFirmaVidSigner from '@/components/SelectorPosicionFirmaVidSigner.vue'

const props = defineProps({
  esquema: { type: Object, required: true },
  registroSeleccionado: { type: Object, required: true },
  showNotification: { type: Function, required: true },
  id: { type: [String, Number], required: false }
})

const emit = defineEmits(['actualizar-registro'])

// Estados principales
const contactosDisponibles = ref([])
const firmantesSeleccionados = ref([])
const busquedaFirmantes = ref('')
const cargandoFirmantes = ref(false)
const mostrarModal = ref(false)
const enviando = ref(false)
const fechaLimite = ref('')
const respuestaVidsigner = ref(null)
const enPosicionFirma = ref(false)
const enResumen = ref(false)
const posicionesResumen = ref([])

// Colores para los marcadores
const colores = [
  "#74b9ff", "#a29bfe", "#55efc4", "#81ecec", "#ff9ff3",
  "#fd79a8", "#ff7675", "#b2bec3", "#fab1a0", "#5352ed"
]

// Estados de selección
const posicionesFirmantes = shallowRef([])
const selectorPosicionRef = ref(null)

// Computed
const firmantesSeleccionadosIds = computed(() => firmantesSeleccionados.value.map(f => f.id))

const firmantesFiltrados = computed(() => {
  const term = busquedaFirmantes.value.toLowerCase().trim()
  const lista = Array.isArray(contactosDisponibles.value) ? contactosDisponibles.value : []
  if (!term) return lista
  return lista.filter(contacto => {
    const nombre = `${contacto.nombre || ''} ${contacto.apellidos || ''}`.toLowerCase()
    const email = (contacto.email || '').toLowerCase()
    const empresa = (contacto.empresa || '').toLowerCase()
    return nombre.includes(term) || email.includes(term) || empresa.includes(term)
  })
})

const todasPosicionesListas = computed(() => {
  if (!selectorPosicionRef.value) return false
  return selectorPosicionRef.value.todasPosicionesListas || false
})

// Funciones de contactos
const cargarContactos = async () => {
  cargandoFirmantes.value = true
  try {
    const response = await axiosInstance.get('/api/contactos')
    contactosDisponibles.value = response.data?.data || []
  } catch (error) {
    console.error('Error al cargar contactos:', error)
    props.showNotification('Error al cargar los contactos', 'alert-danger')
  } finally {
    cargandoFirmantes.value = false
  }
}

const recargarContactos = () => {
  cargarContactos()
}

const alternarFirmante = (contacto) => {
  if (!contacto || !contacto.id) return
  
  try {
    const idx = firmantesSeleccionados.value.findIndex(f => f.id === contacto.id)
    if (idx >= 0) {
      firmantesSeleccionados.value = firmantesSeleccionados.value.filter(f => f.id !== contacto.id)
    } else {
      firmantesSeleccionados.value = [...firmantesSeleccionados.value, contacto]
    }
    
    setTimeout(() => {
      try {
        const seleccionado = props.registroSeleccionado?.value || props.registroSeleccionado
        const firmantesIds = firmantesSeleccionados.value.map(f => f.id)
        emit('actualizar-registro', { ...seleccionado, firmantes: firmantesIds })
      } catch (error) {
        console.warn('[alternarFirmante] Error al emitir (ignorado):', error.message)
      }
    }, 0)
  } catch (error) {
    console.warn('[alternarFirmante] Error al alternar firmante (ignorado):', error.message)
  }
}

const cerrarModal = () => {
  mostrarModal.value = false
}

// Funciones de selección
const continuarFirma = async () => {
  
  // Inicializar array de posiciones
  posicionesFirmantes.value = firmantesSeleccionados.value.map((f, idx) => ({
    firmante: f,
    posicion: null,
    ordenFirma: idx + 1
  }))
  
  enPosicionFirma.value = true
  await nextTick()
}

const volverASeleccion = () => {
  enPosicionFirma.value = false
}

const mostrarResumen = () => {
  if (!todasPosicionesListas.value || !selectorPosicionRef.value) return
  
  // Obtener las posiciones del componente hijo
  posicionesResumen.value = selectorPosicionRef.value.obtenerPosiciones()
  
  enResumen.value = true
}

const volverAPosiciones = () => {
  enResumen.value = false
}

const confirmarEnvio = async () => {
  if (posicionesResumen.value.length === 0 || enviando.value) return
  const seleccionado = props.registroSeleccionado?.value || props.registroSeleccionado
  if (!seleccionado?.id) {
    props.showNotification('No hay documento seleccionado', 'alert-warning')
    return
  }
  enviando.value = true
  respuestaVidsigner.value = null
  try {
    const usuariosIds = posicionesResumen.value.map(item => item.firmante.id)
    
    // Construir el payload con posiciones
    const posiciones = {}
    posicionesResumen.value.forEach(item => {
      if (item.posicion) {
        posiciones[item.firmante.id] = {
          pagina: item.posicion.pagina,
          x: item.posicion.px,
          y: item.posicion.py,
          ancho: 40, // Valor por defecto
          alto: 20   // Valor por defecto
        }
      }
    })
    
    const payload = {
      metodo_firma: 'vidsigner',
      contactos_ids: usuariosIds,
      fecha_limite: fechaLimite.value || null,
      posicion_firma: Object.keys(posiciones).length > 0 ? posiciones[usuariosIds[0]] : undefined
    }
    
    const esquemaId = props.id || props.esquema?.id
    const response = await axiosInstance.post(`/api/firmarDocumento/${esquemaId}/${seleccionado.id}`, payload)
    respuestaVidsigner.value = response.data
    props.showNotification('Documento enviado a VidSigner', 'alert-success')
  } catch (error) {
    console.error('Error al enviar a VidSigner:', error)
    const msg = error?.response?.data?.error || 'No se pudo enviar a VidSigner'
    props.showNotification(msg, 'alert-danger')
  } finally {
    enviando.value = false
  }
}

// Handlers de eventos del componente hijo
const onPosicionGuardada = () => {
  // La posición ya está guardada en el componente hijo
}

const onPosicionesLimpias = () => {
  // Las posiciones ya están limpiadas en el componente hijo
}

// Funciones auxiliares
const abrirAgendaContactos = () => {
  // Abrir la agenda de contactos en una nueva pestaña
  window.open('/agenda', '_blank')
}

// Lifecycle
onMounted(() => {
  cargarContactos()
})
</script>

<style scoped>
.selected-scroll {
  max-height: 300px;
  overflow-y: auto;
}
.panel-compact {
  background: #fafafa;
  border-radius: 12px;
  padding: 10px;
}
</style>

